// Runtime lane detection for the dev-only banner and the boot warning.
//
// Lanes:
//   configured  real Firebase project keys (VITE_FIREBASE_*), talks to navig8atlas
//   emulators   VITE_FIREBASE_EMULATORS=true on loopback (demo-navig8 emulators)
//   e2e         VITE_ENV=e2e build: Firebase is stubbed entirely
//   local       no keys: in-memory demo lane, Storage/Auth/callables disabled
//
// Silent fallbacks are what let bugs hide (the Documents hub only broke when
// Firebase was configured). In development we say which lane is active.

import { isE2ERuntimeEnv, isEmulatorRuntimeEnv, isNavig8FirebaseConfigured } from "../firebase/runtimeGuard";

const REQUIRED_KEYS = ["REACT_APP_FIREBASE_PROJECT_ID", "REACT_APP_FIREBASE_API_KEY", "REACT_APP_FIREBASE_APP_ID"];
const PLACEHOLDERS = new Set(["", "YOUR_API_KEY", "your_api_key_here", "YOUR_APP_ID", "your-project-id"]);

export function missingFirebaseKeys(env = process.env) {
  return REQUIRED_KEYS.filter((key) => !env[key] || PLACEHOLDERS.has(env[key]) || /^1:123456789:web:/.test(env[key]));
}

export function detectRuntimeMode(env = process.env, hostname = typeof window !== "undefined" ? window.location.hostname : "") {
  if (isE2ERuntimeEnv(env)) return { lane: "e2e", label: "E2E build", detail: "Firebase stubbed; demo lane only.", missing: [] };
  if (isEmulatorRuntimeEnv(env, hostname)) return { lane: "emulators", label: "Emulators", detail: "demo-navig8 · Auth 9099 · Firestore 8080 · Storage 9199 · Functions 5001", missing: [] };
  if (isNavig8FirebaseConfigured(env)) return { lane: "configured", label: "Firebase configured", detail: `${env.REACT_APP_FIREBASE_PROJECT_ID} · ${env.REACT_APP_ENV || "development"}`, missing: [] };
  const missing = missingFirebaseKeys(env);
  return {
    lane: "local",
    label: "Local demo mode",
    detail: "No Firebase keys: Storage, Auth, Firestore, and callables are disabled. Bugs in those paths will not reproduce here.",
    missing,
  };
}

export function isDevelopmentRuntime(env = process.env) {
  return env.NODE_ENV === "development";
}

let warned = false;
export function warnRuntimeModeOnce(env = process.env) {
  if (warned || !isDevelopmentRuntime(env)) return null;
  warned = true;
  const mode = detectRuntimeMode(env);
  if (mode.lane === "local") {
    // eslint-disable-next-line no-console
    console.warn(`[NAVIG8] ${mode.label}: ${mode.detail} Missing: ${mode.missing.join(", ") || "none"}. Use \`npm run start:emulators\` (see README) or add a .env.`);
  } else {
    // eslint-disable-next-line no-console
    console.info(`[NAVIG8] Runtime lane: ${mode.label} (${mode.detail})`);
  }
  return mode;
}
